import {
  DefaultError,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  AttachNewSaleTagReq,
  AttachNewSaleTagRes,
  AttachSaleTagReq,
  AttachSaleTagRes,
  AuthorizeCardPaymentReq,
  AuthorizeCardPaymentRes,
  CalculatePartialCloseoutTotalsReq,
  CalculatePartialCloseoutTotalsRes,
  CalculateReturnTotalsReq,
  CalculateReturnTotalsRes,
  CloseOutSaleReq,
  CloseOutSaleRes,
  DetachSaleTagReq,
  DetachSaleTagRes,
  DuplicateSaleReq,
  DuplicateSaleRes,
  EditSaleDateReq,
  EditSaleDateRes,
  EditSaleDueDateReq,
  EditSaleDueDateRes,
  EditSaleDiscountDateReq,
  EditSaleDiscountDateRes,
  EditSaleDesignatedClerkReq,
  EditSaleDesignatedClerkRes,
  EmailSalePDFReq,
  EmailSalePDFRes,
  GetIsEditingCompletedSaleAllowedReq,
  GetIsEditingCompletedSaleAllowedRes,
  GetSaleCountsByStatusReq,
  GetSaleCountsByStatusRes,
  GetSalePDFReq,
  GetSalePDFRes,
  GetSaleReq,
  GetSaleRes,
  GetScannedSaleReq,
  GetScannedSaleRes,
  ListCCPRebateReportUploadsReq,
  ListCCPRebateReportUploadsRes,
  ReturnSaleReq,
  ReturnSaleRes,
  SetCardReaderDisplayReq,
  SetCardReaderDisplayRes,
  SyncCartPricesReq,
  SyncCartPricesRes,
  UpdateSaleProductCostReq,
  UpdateSaleProductCostRes,
  UpdateSaleProductCostsFromCCPRebatesReq,
  UpdateSaleProductCostsFromCCPRebatesRes,
  UpdateSaleTypeReq,
  UpdateSaleTypeRes,
  UpdateSoldSaleDetailsReq,
  UpdateSoldSaleDetailsRes,
  UpdateSoldSaleProductReq,
  UpdateSoldSaleProductRes,
  UpdateSpecialOrderProductPriceAndCostReq,
  UpdateSpecialOrderProductPriceAndCostRes,
  UpsertSaleV3Req,
  UpsertSaleV3Res,
  VoidCompletedSaleReq,
  VoidCompletedSaleRes,
  CalculateCardSurchargeReq,
  CalculateCardSurchargeRes,
  CalculateCardSurchargeDisplayInfoReq,
  CalculateCardSurchargeDisplayInfoRes,
  RefundDepositReq,
  RefundDepositRes,
  RefundInteracPaymentReq,
  RefundInteracPaymentRes,
} from "gen/api/sale/service_pb"
import { SaleServiceClient } from "gen/api/sale/service_pb.client"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Tag } from "gen/tag/models_pb"
import { UUID } from "shared-ts/classes/uuid"

import { apiSlice, transport } from "./apiSlice"

type OptionalReturnSyncCartPricesReq = Omit<
  SyncCartPricesReq,
  "productsToReturn" | "productsV2ToReturn"
> &
  Partial<SyncCartPricesReq>

const s = new SaleServiceClient(transport)

interface SaleTagT {
  type: "Sale"
  id: string
}

const makeSaletag = (saleId?: PBUUID): SaleTagT => {
  return {
    type: "Sale",
    id: UUID.idString(saleId),
  }
}

interface SalePDFTagT {
  type: "SalePDF"
  id: string
}

const makeSalePDFTag = (saleId?: PBUUID): SalePDFTagT => {
  return {
    type: "SalePDF",
    id: UUID.idString(saleId),
  }
}

export const SaleFixedCacheKeys = {
  AUTHORIZE_CARD: "shared-authorize-card",
  SYNC_CART_PRICES: "shared-sync-cart-prices",
  UPSERT_SALE: "shared-upsert-sale",
} as const

export const salesApiSlice = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "SaleCountsByStatus",
      "Customer",
      "CustomerCards",
      "Product",
      "Sale",
      "Location",
      "CustomerAging",
      "TaxEntries",
      "ColorxTintedProduct",
      "JournalEntries",
      "ProfitAndLoss",
      "Return",
      "ReturnTotal",
      "PartialCloseoutTotals",
      "GiftCard",
      "GiftCardHistory",
      "GetGiftCardHistoryPDF",
      "CCPRebateReportUploads",
      "SalePDF",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      authorizeCardPayment: builder.mutation<
        AuthorizeCardPaymentRes,
        AuthorizeCardPaymentReq
      >({
        query: (r) => s.authorizeCardPayment(r),
      }),
      emailSalePDF: builder.mutation<EmailSalePDFRes, EmailSalePDFReq>({
        query: (r) => s.emailSalePDF(r),
      }),
      getSale: builder.query<GetSaleRes, GetSaleReq>({
        query: (r) => s.getSale(r),
        providesTags: (_response, _error, request) => [makeSaletag(request.id)],
      }),
      getScannedSale: builder.query<GetScannedSaleRes, GetScannedSaleReq>({
        query: (r) => s.getScannedSale(r),
      }),
      getSaleCountsByStatus: builder.query<
        GetSaleCountsByStatusRes,
        GetSaleCountsByStatusReq
      >({
        query: (r) => s.getSaleCountsByStatus(r),
        providesTags: ["SaleCountsByStatus"],
      }),
      getSalePDF: builder.query<GetSalePDFRes, GetSalePDFReq>({
        query: (r) => s.getSalePDF(r),
        providesTags: (_response, _error, request) => [
          makeSalePDFTag(request.id),
        ],
      }),
      setCardReaderDisplay: builder.mutation<
        SetCardReaderDisplayRes,
        SetCardReaderDisplayReq
      >({
        query: (r) => s.setCardReaderDisplay(r),
      }),
      syncCartPrices: builder.mutation<
        SyncCartPricesRes,
        OptionalReturnSyncCartPricesReq
      >({
        query: (r) => s.syncCartPrices(SyncCartPricesReq.create(r)),
      }),
      upsertSaleV3: builder.mutation<UpsertSaleV3Res, UpsertSaleV3Req>({
        query: (r) => s.upsertSaleV3(UpsertSaleV3Req.create(r)),
        invalidatesTags: (_response, _error, request) => [
          makeSalePDFTag(request.id),
          "SaleCountsByStatus",
          "Location",
          "CustomerAging",
          "TaxEntries",
          "JournalEntries",
          "ProfitAndLoss",
          "ColorxTintedProduct",
          "PartialCloseoutTotals",
          ...Object.values(request.productsV2)
            .sort((a, b) => a.position - b.position)
            .flatMap(({ productId }) => {
              return productId
                ? [
                    {
                      type: "Product" as const,
                      id: UUID.fromPB(productId).toString(),
                    },
                  ]
                : []
            }),
          ...(request.customerId
            ? [
                {
                  type: "CustomerCards" as const,
                  id: UUID.fromPB(request.customerId).toString(),
                },
                {
                  type: "Customer" as const,
                  id: UUID.fromPB(request.customerId).toString(),
                },
              ]
            : []),
          // Invalidate gift cards used in tender submissions
          ...request.tenderSubmissions.flatMap((tsm) =>
            tsm.giftCard?.giftCardId
              ? [
                  {
                    type: "GiftCard" as const,
                    id: UUID.idString(tsm.giftCard.giftCardId),
                  },
                  {
                    type: "GiftCardHistory" as const,
                    id: UUID.idString(tsm.giftCard.giftCardId),
                  },
                  {
                    type: "GetGiftCardHistoryPDF" as const,
                    id: UUID.idString(tsm.giftCard.giftCardId),
                  },
                ]
              : []
          ),
          ...Object.values(request.productsV2).flatMap(({ giftCardId }) =>
            giftCardId
              ? [
                  {
                    type: "GiftCard" as const,
                    id: UUID.idString(giftCardId),
                  },
                  {
                    type: "GiftCardHistory" as const,
                    id: UUID.idString(giftCardId),
                  },
                  {
                    type: "GetGiftCardHistoryPDF" as const,
                    id: UUID.idString(giftCardId),
                  },
                ]
              : []
          ),
        ],
      }),
      closeOutSale: builder.mutation<CloseOutSaleRes, CloseOutSaleReq>({
        query: (r) => s.closeOutSale(r),
        invalidatesTags: (_response, _error, request) => [
          makeSalePDFTag(request.id),
          "SaleCountsByStatus",
          "Customer",
          "Location",
          "CustomerAging",
          "TaxEntries",
          "ColorxTintedProduct",
          "JournalEntries",
          "ProfitAndLoss",
          "Product",
          "PartialCloseoutTotals",
        ],
      }),
      updateSoldSaleDetails: builder.mutation<
        UpdateSoldSaleDetailsRes,
        UpdateSoldSaleDetailsReq
      >({
        query: (r) => s.updateSoldSaleDetails(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.id),
        ],
      }),
      updateSoldSaleProduct: builder.mutation<
        UpdateSoldSaleProductRes,
        UpdateSoldSaleProductReq
      >({
        query: (r) => s.updateSoldSaleProduct(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.saleId),
        ],
      }),
      updateSaleProductCost: builder.mutation<
        UpdateSaleProductCostRes,
        UpdateSaleProductCostReq & { saleId?: PBUUID }
      >({
        query: (r) => s.updateSaleProductCost(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.saleId),
        ],
      }),
      updateSpecialOrderProductPriceAndCost: builder.mutation<
        UpdateSpecialOrderProductPriceAndCostRes,
        UpdateSpecialOrderProductPriceAndCostReq & { saleId?: PBUUID }
      >({
        query: (r) => s.updateSpecialOrderProductPriceAndCost(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.saleId),
          "PartialCloseoutTotals",
        ],
      }),
      updateSaleProductCostsFromCCPRebates: builder.mutation<
        UpdateSaleProductCostsFromCCPRebatesRes,
        UpdateSaleProductCostsFromCCPRebatesReq
      >({
        query: (r) => s.updateSaleProductCostsFromCCPRebates(r),
        invalidatesTags: ["CCPRebateReportUploads"],
      }),
      listCCPRebateReportUploads: builder.query<
        ListCCPRebateReportUploadsRes,
        ListCCPRebateReportUploadsReq
      >({
        query: (r) => s.listCCPRebateReportUploads(r),
        providesTags: ["CCPRebateReportUploads"],
      }),
      duplicateSale: builder.mutation<DuplicateSaleRes, DuplicateSaleReq>({
        query: (r) => s.duplicateSale(r),
        invalidatesTags: (_response, _error) => ["SaleCountsByStatus"],
      }),
      // Returns
      returnSale: builder.mutation<ReturnSaleRes, ReturnSaleReq>({
        query: (r) => s.returnSale(r),
        invalidatesTags: (_response, _error, request) => [
          "Location",
          makeSaletag(request.saleId),
          "Customer",
          "CustomerAging",
          "ReturnTotal",
          ...Object.keys(request.returnProductsByReturnFromSaleProductId).map(
            (id) => ({
              type: "Product" as const,
              id: id,
            })
          ),
          ...(request.tenderSubmission?.giftCard?.giftCardId
            ? [
                {
                  type: "GiftCard" as const,
                  id: UUID.fromPB(
                    request.tenderSubmission.giftCard.giftCardId
                  ).toString(),
                },
                {
                  type: "GiftCardHistory" as const,
                  id: UUID.fromPB(
                    request.tenderSubmission.giftCard.giftCardId
                  ).toString(),
                },
                {
                  type: "GetGiftCardHistoryPDF" as const,
                  id: UUID.fromPB(
                    request.tenderSubmission.giftCard.giftCardId
                  ).toString(),
                },
              ]
            : []),
        ],
      }),
      calculateReturnTotals: builder.query<
        CalculateReturnTotalsRes,
        CalculateReturnTotalsReq
      >({
        query: (r) => s.calculateReturnTotals(r),
        providesTags: ["ReturnTotal"],
      }),
      calculatePartialCloseoutTotals: builder.query<
        CalculatePartialCloseoutTotalsRes,
        CalculatePartialCloseoutTotalsReq
      >({
        query: (r) => s.calculatePartialCloseoutTotals(r),
        providesTags: ["PartialCloseoutTotals"],
      }),
      voidCompletedSale: builder.mutation<
        VoidCompletedSaleRes,
        VoidCompletedSaleReq
      >({
        query: (r) => s.voidCompletedSale(r),
        invalidatesTags: (_response, _error, request) => [
          "SaleCountsByStatus",
          makeSaletag(request.id),
          "CustomerAging",
          "Customer",
        ],
      }),
      getIsEditingCompletedSaleAllowed: builder.query<
        GetIsEditingCompletedSaleAllowedRes,
        GetIsEditingCompletedSaleAllowedReq
      >({
        query: (r) => s.getIsEditingCompletedSaleAllowed(r),
      }),
      editSaleDate: builder.mutation<EditSaleDateRes, EditSaleDateReq>({
        query: (r) => s.editSaleDate(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.saleId),
          "SaleCountsByStatus",
          "CustomerAging",
          "Customer",
        ],
      }),
      editSaleDueDate: builder.mutation<EditSaleDueDateRes, EditSaleDueDateReq>(
        {
          query: (r) => s.editSaleDueDate(r),
          invalidatesTags: (_response, _error, request) => [
            makeSaletag(request.saleId),
            "CustomerAging",
            "Customer",
          ],
        }
      ),
      editSaleDiscountDate: builder.mutation<
        EditSaleDiscountDateRes,
        EditSaleDiscountDateReq
      >({
        query: (r) => s.editSaleDiscountDate(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.saleId),
          "CustomerAging",
          "Customer",
        ],
      }),
      editSaleDesignatedClerk: builder.mutation<
        EditSaleDesignatedClerkRes,
        EditSaleDesignatedClerkReq
      >({
        query: (r) => s.editSaleDesignatedClerk(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.saleId),
        ],
      }),
      updateSaleType: builder.mutation<UpdateSaleTypeRes, UpdateSaleTypeReq>({
        query: (r) => s.updateSaleType(r),
        invalidatesTags: (_response, _error, request) => [
          makeSaletag(request.saleId),
        ],
      }),
      calculateCardSurcharge: builder.query<
        CalculateCardSurchargeRes,
        CalculateCardSurchargeReq
      >({
        query: (r) => s.calculateCardSurcharge(r),
      }),
      calculateCardSurchargeDisplayInfo: builder.query<
        CalculateCardSurchargeDisplayInfoRes,
        CalculateCardSurchargeDisplayInfoReq
      >({
        query: (r) => s.calculateCardSurchargeDisplayInfo(r),
      }),
      refundDeposit: builder.mutation<RefundDepositRes, RefundDepositReq>({
        query: (r) => s.refundDeposit(r),
        invalidatesTags: (_response, _error, request) => [
          makeSalePDFTag(request.id),
          "SaleCountsByStatus",
        ],
      }),
      refundInteracPayment: builder.mutation<
        RefundInteracPaymentRes,
        RefundInteracPaymentReq
      >({
        query: (r) => s.refundInteracPayment(r),
      }),
    }),
  })

export const {
  usePrefetch: useSalePrefetch,
  useGetSaleQuery,
  useLazyGetScannedSaleQuery,
  useLazyGetSaleQuery,
  useGetSaleCountsByStatusQuery,
  useLazyGetSaleCountsByStatusQuery,
  useUpsertSaleV3Mutation,
  useReturnSaleMutation,
  useSyncCartPricesMutation,
  useEmailSalePDFMutation,
  useGetSalePDFQuery,
  useLazyGetSalePDFQuery,
  useUpdateSoldSaleProductMutation,
  useUpdateSoldSaleDetailsMutation,
  useAuthorizeCardPaymentMutation,
  useSetCardReaderDisplayMutation,
  useUpdateSaleProductCostMutation,
  useUpdateSpecialOrderProductPriceAndCostMutation,
  useDuplicateSaleMutation,
  useLazyCalculateReturnTotalsQuery,
  useCalculatePartialCloseoutTotalsQuery,
  useCloseOutSaleMutation,
  useVoidCompletedSaleMutation,
  useGetIsEditingCompletedSaleAllowedQuery,
  useCalculateCardSurchargeQuery,
  useEditSaleDateMutation,
  useEditSaleDueDateMutation,
  useEditSaleDiscountDateMutation,
  useEditSaleDesignatedClerkMutation,
  useUpdateSaleTypeMutation,
  useCalculateCardSurchargeDisplayInfoQuery,
  useRefundDepositMutation,
  useRefundInteracPaymentMutation,
  useUpdateSaleProductCostsFromCCPRebatesMutation,
  useListCCPRebateReportUploadsQuery,
} = salesApiSlice

export function useListSaleTagsQuery(saleId: PBUUID) {
  return useQuery({
    queryKey: ["sale-tags", UUID.idString(saleId)],
    queryFn: async () => {
      const { response } = await s.listSaleTags({ saleId })
      return response
    },
  })
}

export function useAttachNewSaleTagMutation() {
  const queryClient = useQueryClient()
  return useMutation<AttachNewSaleTagRes, DefaultError, AttachNewSaleTagReq>({
    mutationFn: async (req) => {
      const { response } = await s.attachNewSaleTag(req)
      return response
    },
    onSettled: (_res, _err, { saleId }) =>
      queryClient.invalidateQueries({
        queryKey: ["sale-tags", UUID.idString(saleId)],
      }),
  })
}

export type OptimisticAttachSaleTagReq = AttachSaleTagReq & { tag: Tag }

export function useAttachSaleTagMutation() {
  const queryClient = useQueryClient()
  return useMutation<
    AttachSaleTagRes,
    DefaultError,
    OptimisticAttachSaleTagReq
  >({
    mutationFn: async (req) => {
      const { response } = await s.attachSaleTag(req)
      return response
    },
    onSettled: (_res, _err, { saleId }) =>
      queryClient.invalidateQueries({
        queryKey: ["sale-tags", UUID.idString(saleId)],
      }),
  })
}

export function useDetachSaleTagMutation() {
  const queryClient = useQueryClient()
  return useMutation<DetachSaleTagRes, DefaultError, DetachSaleTagReq>({
    mutationFn: async (req) => {
      const { response } = await s.detachSaleTag(req)
      return response
    },
    onSettled: (_res, _err, { saleId }) =>
      queryClient.invalidateQueries({
        queryKey: ["sale-tags", UUID.idString(saleId)],
      }),
  })
}
